import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { EmojiEventsOutlined as TrophyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Contest } from '../../models/types';
import { apiService } from '../../services/api.service';
import { SurfaceCard } from '../ui/SurfaceCard';
import { EmptyState } from '../ui/EmptyState';
import { ContestListRow, getContestListStatus } from './ContestListRow';

interface ContestListProps {
  groupId: string;
}

// A single group's challenges, shown on the group page.
export const ContestList: React.FC<ContestListProps> = ({ groupId }) => {
  const { t } = useTranslation('contests');
  const [contests, setContests] = useState<Contest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchContests = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${apiService.API_BASE_URL}/groups/${groupId}/contests`, {
          ...apiService.getRequestOptions(),
        });
        if (!response.ok) {
          throw new Error('Failed to fetch contests');
        }
        setContests(await response.json());
      } catch (err) {
        console.error('Error fetching contests:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchContests();
  }, [groupId]);

  const now = new Date();
  // Active first, then upcoming, then ended; newest start first within a bucket.
  const rows = contests
    .map(contest => ({ contest, status: getContestListStatus(contest, now) }))
    .sort((a, b) => a.status.rank - b.status.rank || new Date(b.contest.startAt).getTime() - new Date(a.contest.startAt).getTime());

  return (
    <SurfaceCard title={t('list.title')} bodyPadding={0}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: 'center', color: 'var(--danger)', fontSize: 14 }}>{t('list.loadError')}</Box>
      ) : rows.length === 0 ? (
        <EmptyState icon={<TrophyIcon />} title={t('list.emptyTitle')} description={t('list.emptyDescription')} />
      ) : (
        rows.map(({ contest, status }) => <ContestListRow key={contest._id} contest={contest} status={status} />)
      )}
    </SurfaceCard>
  );
};
